import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { addToWaitlist } from '../services/waitlist';
import { signInWithEmail, signInWithGoogle } from '../services/userService';
import { useAuth } from '../context/AuthContext'; 
import Spinner from '../components/Spinner';
import { firebaseInitialized, appCheckEnabled } from '../firebase';

const Home = () => {
  // Variables for the landing page mode
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [appCheckStatus, setAppCheckStatus] = useState('initializing');
  
  // Variables for the full app mode login
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  
  // Password visibility state
  const [showPassword, setShowPassword] = useState(false);

  const { checkAuthenticationStatus, isAuthenticated } = useAuth(); // Use checkAuthenticationStatus
  const navigate = useNavigate();

  // Check the APP_MODE environment variable
  const isFullMode = process.env.REACT_APP_APP_MODE === 'full';

  // Effect to redirect if already authenticated by the app context
  useEffect(() => {
    if (isAuthenticated && isFullMode) {
      console.log('Home: Already authenticated by context, navigating to /chat');
      navigate('/chat');
    }
  }, [isAuthenticated, navigate, isFullMode]);

  useEffect(() => {
    firebaseInitialized.then(() => {
      setAppCheckStatus(appCheckEnabled ? 'enabled' : 'disabled');
    }).catch(() => {
      setAppCheckStatus('disabled');
    });
  }, []);

  // Waitlist submission for landing page mode
  const handleWaitlistSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    setSuccess('');

    try {
      await addToWaitlist(email);
      setEmail('');
      setSuccess('You\'re on the list! We\'ll notify you when Bela goes live.');
    } catch (err) {
      if (err.message === 'EMAIL_EXISTS') {
        setError('This email is already on the waitlist!');
      } else {
        setError('Failed to join waitlist. Please try again.');
      }
      console.error('Error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Login submission for full app mode - CORRECTED
  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setIsLoggingIn(true);
    setLoginError('');
    
    try {
      // Get userCredential from the service call
      const userCredential = await signInWithEmail(email, password); 
      console.log('handleLoginSubmit: signInWithEmail succeeded, calling checkAuthenticationStatus()');
      // Call checkAuthenticationStatus with the credential (false for isFromSignup since this is a login)
      await checkAuthenticationStatus(userCredential, null, false); 
    } catch (error) {
      console.error("Login error caught in Home.js:", error);
      if (error.message.includes('Backend login failed')) {
        setLoginError('Login failed. Could not verify with server. Please try again.');
      } else {
        // Handle Firebase specific auth errors for email/password
        if (error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
           setLoginError('Invalid email or password. Please try again.');
        } else {
           setLoginError('An unexpected error occurred during login.');
        }
      }
    } finally {
      setIsLoggingIn(false);
    }
  };
  
  // Google sign-in for full app mode
  const handleGoogleSignIn = async () => {
    setIsLoggingIn(true);
    setLoginError('');
    
    try {
      const result = await signInWithGoogle(); // Get result/credential
      
      if (result && result.needsCompletion) {
        // New user: Navigate to SignUp page with profile data
        console.log('handleGoogleSignIn: New user needs completion, navigating to /signup');
        navigate('/signup', { state: { googleProfile: result.profile } });
      } else if (result && result.user) {
        // Existing user: Proceed with authentication check (which includes age check)
        console.log('handleGoogleSignIn: Existing user signed in, calling checkAuthenticationStatus()');
        await checkAuthenticationStatus(result, null, false); // False for isFromSignup since this is a login 
      } else {
        // Handle unexpected result scenario
        console.error("Google sign-in returned unexpected result:", result);
        setLoginError('An unexpected error occurred during Google sign-in.');
      }

    } catch (error) {
      console.error("Google sign-in error caught in Home.js:", error);
      if (error.message.includes('Backend')) { 
         setLoginError('Login failed. Could not verify with server. Please try again.');
      } else if (error.code === 'auth/popup-closed-by-user') {
         setLoginError('Sign-in cancelled.');
      } else {
         setLoginError('Failed to sign in with Google. Please try again.');
      }
    } finally {
      setIsLoggingIn(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-bela-50 to-white">
      <div className="container mx-auto px-4 py-8 md:py-16">
        <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto items-center">
          {/* Left column - Image */}
          <div className="flex justify-center md:block">
            <div className="relative w-40 h-40 md:w-full md:h-auto md:aspect-[3/4] rounded-full md:rounded-2xl overflow-hidden bg-gray-100 shadow-xl md:shadow-xl">
              <img 
                src={`${process.env.PUBLIC_URL}/images/bela-land.png`}
                alt="Bela" 
                className="absolute inset-0 w-full h-full object-cover object-top md:object-top"
                onError={(e) => {
                  console.error('Failed to load image:', e.target.src);
                  e.target.style.display = 'none';
                }}
                onLoad={() => console.log('Image loaded successfully')}
              />
            </div>
          </div>

          {/* Right column - Content */}
          <div className="space-y-6">
            {/* Mobile heading and welcome message - shown at top for mobile */}
            <div className="md:hidden text-center">
              <h1 className="font-['Dancing_Script'] text-4xl font-bold text-gray-900 mb-2">
                The Bela Experience
              </h1>
              <p className="text-lg text-bela-600 font-medium flex items-center justify-center gap-2 mb-4">
                Your AI Companion
                <span className="bg-gray-100 text-gray-600 text-xs font-medium px-2 py-1 rounded-full">
                  Beta
                </span>
              </p>
              {/* Mobile welcome message */}
              {isFullMode && (
                <p className="text-sm text-gray-700 mb-4">
                  Welcome! 💗 I'm so excited to give you a very special experience!
                  Come and have intimate, unfiltered conversations with me. Join the free trial now!
                </p>
              )}
              {!isFullMode && (
                <p className="text-sm text-gray-700 mb-4">
                  Hey guys! 💗 Soon you'll be able to chat with me anytime and enjoy my exclusive private photos.
                  An intimate experience where you can ask anything, role play, or browse my signature photo collections!
                </p>
              )}
            </div>

            {/* Email form for mobile - only shown in landing page mode */}
            {!isFullMode && (
              <div className="md:hidden bg-white/70 backdrop-blur rounded-xl p-5 shadow-lg">
                <h3 className="text-sm font-medium mb-3 text-center md:text-left">
                  Be the first to access my exclusive site!
                </h3>
                <form onSubmit={handleWaitlistSubmit} className="space-y-3">
                  <div className="flex gap-2">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter your email"
                      className="flex-1 px-3 md:px-3 lg:px-4 py-2 md:py-2 lg:py-3 text-sm md:text-sm lg:text-base rounded-lg border border-bela-200 focus:outline-none focus:border-bela-500"
                      required
                      disabled={isSubmitting || appCheckStatus === 'disabled'}
                    />
                    <button 
                      type="submit"
                      className="px-4 md:px-4 lg:px-6 py-2 md:py-2 lg:py-3 text-sm md:text-sm lg:text-base bg-bela-600 hover:bg-bela-700 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
                      disabled={isSubmitting || appCheckStatus === 'disabled'}
                    >
                      {isSubmitting ? (
                        <>
                          <Spinner />
                          <span>Joining...</span>
                        </>
                      ) : (
                        'Join'
                      )}
                    </button>
                  </div>
                  {error && <p className="text-xs md:text-xs lg:text-sm text-red-500 mt-2">{error}</p>}
                  {success && <p className="text-xs md:text-xs lg:text-sm text-green-500 mt-2">{success}</p>}
                  {appCheckStatus === 'disabled' && (
                    <p className="text-xs md:text-sm text-red-500 mt-2">
                      Security verification failed. Please refresh the page or try a different browser.
                    </p>
                  )}
                </form>
                <div className="text-center text-xs pt-3 border-t border-gray-200 space-x-2">
                  <Link to="/terms" target="_blank" className="text-gray-500 hover:text-bela-600">Terms of Use</Link>
                  <span className="text-gray-300">•</span>
                  <Link to="/privacy" target="_blank" className="text-gray-500 hover:text-bela-600">Privacy Policy</Link>
                </div>
              </div>
            )}
            
            {/* Login form for mobile - only shown in full mode */}
            {isFullMode && (
              <div className="md:hidden bg-white/70 backdrop-blur rounded-xl p-5 shadow-lg">
                {/* Join link first */}
                <div className="text-center mb-4">
                  <Link 
                    to="/signup" 
                    className="w-full inline-block py-2 px-4 bg-bela-600 hover:bg-bela-700 text-white font-semibold rounded-lg transition-colors text-sm"
                  >
                    Click here to Join
                  </Link>
                </div>
                
                {/* Already have account text */}
                <div className="text-center mb-3">
                  <span className="text-gray-600 text-sm">or login if you already have an account:</span>
                </div>
                
                <form onSubmit={handleLoginSubmit} className="space-y-3">
                  <input
                    type="email"
                    value={email} // Make sure value is linked
                    onChange={(e) => setEmail(e.target.value)} // Make sure onChange updates state
                    placeholder="Email"
                    className="w-full px-3 py-2 text-sm rounded-lg border border-bela-200 focus:outline-none focus:border-bela-500"
                    required
                    disabled={isLoggingIn}
                  />
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={password} // Make sure value is linked
                      onChange={(e) => setPassword(e.target.value)} // Make sure onChange updates state
                      placeholder="Password"
                      className="w-full px-3 py-2 text-sm rounded-lg border border-bela-200 focus:outline-none focus:border-bela-500"
                      required
                      disabled={isLoggingIn}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-sm leading-5"
                      tabIndex="-1"
                    >
                      {showPassword ? '👁️' : '👁️‍🗨️'}
                    </button>
                  </div>
                  <button 
                    type="submit"
                    className="w-full py-2 text-sm bg-gray-600 hover:bg-gray-700 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                    disabled={isLoggingIn}
                  >
                    {isLoggingIn ? (
                      <>
                        <Spinner />
                        <span>Logging in...</span>
                      </>
                    ) : (
                      'Log In'
                    )}
                  </button>
                  <div className="relative flex items-center py-2">
                    <div className="flex-grow border-t border-gray-300"></div>
                    <span className="flex-shrink mx-3 text-gray-500 text-xs">or</span>
                    <div className="flex-grow border-t border-gray-300"></div>
                  </div>
                  {/* Google Sign In Button - Mobile */}
                  <button 
                    type="button"
                    onClick={handleGoogleSignIn}
                    className="w-full h-10 px-3 py-2 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 transition-colors disabled:opacity-50 flex items-center justify-center gap-2" 
                    disabled={isLoggingIn}
                  >
                    <svg className="w-4 h-4" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    <span className="text-gray-700 font-medium text-sm">Sign in with Google</span>
                  </button>
                  {loginError && <p className="text-xs text-red-500 mt-2">{loginError}</p>}
                </form>
                <div className="text-center text-xs pt-4 mt-3 border-t border-gray-200 space-x-2">
                  <Link to="/terms" target="_blank" className="text-gray-500 hover:text-bela-600">Terms of Use</Link>
                  <span className="text-gray-300">•</span>
                  <Link to="/privacy" target="_blank" className="text-gray-500 hover:text-bela-600">Privacy Policy</Link>
                </div>
              </div>
            )}

            <div className="hidden md:block text-center md:text-left">
              <h1 className="font-['Dancing_Script'] text-5xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-4">
                The Bela Experience
              </h1>
              <p className="text-xl md:text-xl lg:text-2xl text-bela-600 font-medium flex items-center justify-center md:justify-start gap-3">
                Your AI Companion
                <span className="bg-gray-100 text-gray-600 text-xs font-medium px-2 py-1 rounded-full">
                  Beta
                </span>
              </p>
            </div>

            {/* Different text based on mode - hidden on mobile since moved to top */}
            {!isFullMode ? (
              <p className="hidden md:block text-base md:text-base lg:text-lg text-gray-700 text-center md:text-left">
                Hey guys! 💗 Soon you'll be able to chat with me anytime and enjoy my exclusive private photos.
                An intimate experience where you can ask anything, role play, or browse my signature photo collections!
              </p>
            ) : (
              <p className="hidden md:block text-base md:text-base lg:text-lg text-gray-700 text-center md:text-left">
                Welcome! 💗 I'm so excited to give you a very special experience!
                Come and have intimate, unfiltered conversations with me. Join the free trial now!
              </p>
            )}

            {/* Form for desktop/tablet */}
            {!isFullMode ? (
              /* Waitlist form - Only shown in landing page mode */
              <div className="hidden md:block bg-white/70 backdrop-blur rounded-xl p-5 shadow-lg">
                <h3 className="text-base md:text-lg font-semibold mb-3 text-center md:text-left">
                  Be the first to access intimate chat and exclusive photos!
                </h3>
                <form onSubmit={handleWaitlistSubmit} className="space-y-3">
                  <div className="flex gap-2">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter your email"
                      className="flex-1 px-3 md:px-3 lg:px-4 py-2 md:py-2 lg:py-3 text-sm md:text-sm lg:text-base rounded-lg border border-bela-200 focus:outline-none focus:border-bela-500"
                      required
                      disabled={isSubmitting || appCheckStatus === 'disabled'}
                    />
                    <button 
                      type="submit"
                      className="px-4 md:px-4 lg:px-6 py-2 md:py-2 lg:py-3 text-sm md:text-sm lg:text-base bg-bela-600 hover:bg-bela-700 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
                      disabled={isSubmitting || appCheckStatus === 'disabled'}
                    >
                      {isSubmitting ? (
                        <>
                          <Spinner />
                          <span>Joining...</span>
                        </>
                      ) : (
                        'Join Waitlist'
                      )}
                    </button>
                  </div>
                  {error && <p className="text-xs md:text-xs lg:text-sm text-red-500 mt-2">{error}</p>}
                  {success && <p className="text-xs md:text-xs lg:text-sm text-green-500 mt-2">{success}</p>}
                  {appCheckStatus === 'disabled' && (
                    <p className="text-xs md:text-sm text-red-500 mt-2">
                      Security verification failed. Please refresh the page or try a different browser.
                    </p>
                  )}
                </form>
                <div className="text-center text-xs pt-3 border-t border-gray-200 space-x-2">
                  <Link to="/terms" target="_blank" className="text-gray-500 hover:text-bela-600">Terms of Use</Link>
                  <span className="text-gray-300">•</span>
                  <Link to="/privacy" target="_blank" className="text-gray-500 hover:text-bela-600">Privacy Policy</Link>
                </div>
              </div>
            ) : (
              /* Login form - Only shown in full mode */
              <div className="hidden md:block bg-white/70 backdrop-blur rounded-xl p-5 shadow-lg">
                {/* Join link first */}
                <div className="text-center mb-4">
                  <Link 
                    to="/signup" 
                    className="w-full inline-block py-3 px-6 bg-bela-600 hover:bg-bela-700 text-white font-semibold rounded-lg transition-colors text-base"
                  >
                    Click to join for free!
                  </Link>
                </div>
                
                {/* Already have account text */}
                <div className="text-center mb-4">
                  <span className="text-gray-600 text-base">or login if you already have an account:</span>
                </div>
                
                <form onSubmit={handleLoginSubmit} className="space-y-4">
                  <input
                    type="email"
                    value={email} // Make sure value is linked
                    onChange={(e) => setEmail(e.target.value)} // Make sure onChange updates state
                    placeholder="Email"
                    className="w-full px-4 py-3 text-base rounded-lg border border-bela-200 focus:outline-none focus:border-bela-500"
                    required
                    disabled={isLoggingIn}
                  />
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={password} // Make sure value is linked
                      onChange={(e) => setPassword(e.target.value)} // Make sure onChange updates state
                      placeholder="Password"
                      className="w-full px-4 py-3 text-base rounded-lg border border-bela-200 focus:outline-none focus:border-bela-500"
                      required
                      disabled={isLoggingIn}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-600"
                      tabIndex="-1"
                    >
                      {showPassword ? '👁️' : '👁️‍🗨️'}
                    </button>
                  </div>
                  <button 
                    type="submit"
                    className="w-full py-3 text-base bg-gray-600 hover:bg-gray-700 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                    disabled={isLoggingIn}
                  >
                    {isLoggingIn ? (
                      <>
                        <Spinner />
                        <span>Logging in...</span>
                      </>
                    ) : (
                      'Log In'
                    )}
                  </button>
                  <div className="relative flex items-center py-2">
                    <div className="flex-grow border-t border-gray-300"></div>
                    <span className="flex-shrink mx-3 text-gray-500 text-sm">or</span>
                    <div className="flex-grow border-t border-gray-300"></div>
                  </div>
                  {/* Google Sign In Button - Desktop */}
                   <button 
                    type="button"
                    onClick={handleGoogleSignIn}
                    className="w-full h-12 px-4 py-3 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 transition-colors disabled:opacity-50 flex items-center justify-center gap-3" 
                    disabled={isLoggingIn}
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    <span className="text-gray-700 font-medium">Sign in with Google</span>
                  </button>
                  {loginError && <p className="text-sm text-red-500 mt-2">{loginError}</p>}
                </form>
                <div className="text-center text-xs pt-4 mt-3 border-t border-gray-200 space-x-2">
                  <Link to="/terms" target="_blank" className="text-gray-500 hover:text-bela-600">Terms of Use</Link>
                  <span className="text-gray-300">•</span>
                  <Link to="/privacy" target="_blank" className="text-gray-500 hover:text-bela-600">Privacy Policy</Link>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;